import { useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { InboundLayout, InboundHeaderCard } from "ui-components/Layout";
import { InboundDetailEditButton } from "ui-components/Form";

import ASN from "./components/ASN";
import { headerRender, headerValues, headerValidation } from "./asn-schema";
import { detailRender, detailValues, detailValidation } from "./asn-details-schema";
import ASNDetail from "./components/ASNDetail";
import { InfoDialog, Success, Error_ } from "ui-components/Alert";
import { useMutation, useQuery } from "@tanstack/react-query";
import { asnColumns } from "./columns";
import { useLocation, useNavigate } from "react-router-dom";
import { getASN, editASN } from "services/data.inbound.service";
import { InboundSaveButton } from "ui-components/Form";
import BasicTable2 from "ui-components/tables/BasicTable2";
import { statusLabel, statusObj } from "layouts/views/common/enum";
import { LoadingDialog } from "ui-components/Alert";
import { cancelDetail, getTotalQuantity, updateDetail } from "../common/common";
import { ASNCaption } from "../components/Caption";

function EditASN() {
  const location = useLocation();
  const navigate = useNavigate();
  const { asnNumber, customerCode } = location.state;
  const [formValues, setFormValues] = useState(headerValues);
  const [alert, setAlert] = useState(null);
  const [details, setDetails] = useState([]);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const editASNMutation = useMutation(editASN, {});

  const { isFetching } = useQuery({
    queryKey: ["asn", { asnNumber, customerCode }],
    queryFn: () => getASN({ asnNumber, customerCode }),
    onSuccess: (data) => {
      const { status, asnDetails, recvType, ...asnValues } = data.data.data;
      setFormValues({ ...asnValues, status: statusLabel[status] });
      setDetails(asnDetails.map((detail) => ({ ...detail, status: statusLabel[detail.status] })));
    },
  });

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });
  const {
    control,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
    trigger,
  } = form;
  const headerProps = { headerRender, control, setValue, errors, mode: "EDIT" };

  const detailProps = { details, setDetails };

  const onSubmit = async () => {
    window.scrollTo(0, 0);
    const isValid = await trigger();
    if (!isValid) return;

    const data = getValues();

    if (details.length === 0) {
      setAlert(<Error_ content="Invalid ASN Details" onChange={setAlert} />);
      return;
    }

    const req = {
      ...data,
      asnDetails: details.map((d) => ({ ...d, status: statusObj[d.status] })),
    };

    editASNMutation.mutate(
      { asnNumber, customerCode, req },
      {
        onSuccess: (data) => navigate("/inbound/asn", { state: data.data.message }),
        onError,
      }
    );
  };
  return (
    <InboundLayout lg={12}>
      {isFetching && <LoadingDialog />}
      {alert}
      <InboundHeaderCard>
        <form noValidate>
          <ASN {...headerProps} />
        </form>
      </InboundHeaderCard>

      <Detail {...detailProps} />
      {/* <InboundSubmitButton label="Submit" onClick={onSubmit} disabled={isSubmitting} /> */}
      <InboundSaveButton url="/inbound/asn" onClick={onSubmit} disabled={isSubmitting} />
    </InboundLayout>
  );
}

const Detail = ({ details, setDetails }) => {
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Cancel"],
      type: "actions",
    },
    ...asnColumns,
  ];
  const [alert, setAlert] = useState(null);

  const [formValues, setFormValues] = useState(null);
  const [dialog, setDialog] = useState(null);
  const lineNo = formValues ? formValues.lineNo : details.length + 1;

  const form = useForm({
    defaultValues: { ...detailValues, lineNo },
    values: formValues || { ...detailValues, lineNo },
    resolver: yupResolver(detailValidation),
  });
  const {
    control,
    formState: { errors, isDirty, isSubmitting },
    getValues,
    handleSubmit,
    reset,
  } = form;

  const detailProps = { detailRender, control, errors, getValues, mode: "EDIT" };
  const onSubmit = (data) => {
    setDetails(updateDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Updated" onChange={setAlert} />);
  };
  const onRowClick = (values) => {
    if (formValues !== null && isDirty) {
      setDialog(
        <InfoDialog
          title={`Unsaved changes for Line No ${formValues.lineNo}`}
          content="Do you want to save the changes"
          onOk={() => {
            const data = getValues();
            setDetails(updateDetail(details, data));
            setDialog(null);
            setFormValues(values);
          }}
          onClose={() => {
            setDialog(null);
            setFormValues(values);
          }}
        />
      );
    } else {
      setFormValues(values);
    }
    return true;
  };

  const onCancel = (data) => {
    setDetails(cancelDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Cancelled" onChange={setAlert} />);
  };

  const totalQuantity = getTotalQuantity(details, "qtyExpected");
  const caption = <ASNCaption totalRows={details.length} totalQuantity={totalQuantity} />;

  const tableProps = { columns, rows: details, caption, onRowClick, onCancel, mode: "EDIT" };
  return (
    <InboundHeaderCard sx={{ height: "100%" }}>
      {alert}
      {dialog}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <ASNDetail {...detailProps} />
        {formValues !== null && (
          <InboundDetailEditButton disabled={!isDirty} onClick={() => setFormValues(null)} />
        )}
      </form>

      <BasicTable2 {...tableProps} />
    </InboundHeaderCard>
  );
};

export default EditASN;
